<template>
  <BaseDialog
    data-test-id="editSignup"
    v-bind="$attrs"
    v-model="store.showEditDialog"
    :allow-edit="allowEdit"
    :show-delete="showDelete"
    :table-store-id="TableStoreId.Events"
    :title="
      t('dialogs.headers.signupFor', {
        eventCategoryTitle: eventsStore.getCategoryTitle(item.category)
      })
    "
    :max-width="435"
    :min-height="350"
    @save-item="signup"
  >
    <v-form :fast-fail="false" @submit.prevent ref="signupForm">
      <EventDetails :item="item" />
      <p v-if="item.status === EventStatus.Confirmed" class="cancel-message">
        Click cancel if you can no longer attend this appointment.
      </p>
    </v-form>

    <template v-slot:actions>
      <ButtonSecondary @click="close" :text="t('buttons.close')" />
      <v-spacer />
      <ButtonPrimary
        v-if="item.status === EventStatus.Tentative"
        @click="signup"
        :text="t('buttons.signup')"
      />
      <ButtonPrimary
        v-if="item.status === EventStatus.Confirmed"
        @click="cancelAppointment"
        :text="t('buttons.cancel')"
      />
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { EventStatus } from '@/enums/EventStatus'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import { EventRecord } from '@/models/EventRecord'
import { useSelfSchedulerStore } from '@/stores/db/SelfSchedulerStore'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ButtonPrimary from '../buttons/ButtonPrimary.vue'
import BaseDialog from '../dialogs/BaseDialog.vue'

const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
const store = useSelfSchedulerStore()

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
defineProps({
  showDelete: { type: Boolean, default: false }
})

// REACTIVE VARIABLES
const signupForm = ref<any>()

// COMPUTED PROPERTIES
const allowEdit = computed(() => {
  const startDate = dayjs(item.value.dtStart).format('MM/DD/YYYY')
  const today = dayjs().format('MM/DD/YYYY')
  return dayjs(startDate) >= dayjs(today)
})

const item = computed(() => {
  if (!store.selectedItem) {
    return new EventRecord()
  }
  return store.selectedItem as EventRecord
})

// WATCHERS

// LIFECYCLE HOOKS

// FUNCTIONS
const cancelAppointment = async () => {
  if (store.key && store.selectedItem?.id) {
    const success = await store.cancelAppointment(item.value)
    if (success) {
      store.showEditDialog = false
    }
  }
}

const close = async () => {
  store.showEditDialog = false
}

const signup = async () => {
  const { valid } = await signupForm.value.validate()
  if (!valid) {
    return
  }
  if (store.key && store.selectedItem?.id) {
    const success = await store.signup(store.key, store.selectedItem.id)
    if (success) {
      store.showEditDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
.cancel-message {
  margin: 0;
  padding: 0;
}
.note {
  font-style: italic;
}
</style>
